"use client";

import * as React from "react";
import { useRouter } from "next/navigation";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

import { handleErrors, post } from "@/src/api";
import { showToast } from "@/src/components/toast/showToast";
import { convertPersianToEnglish } from "@/src/tools";
import HelpHint from "@/src/components/HelpHint";
import { Loader2, ShieldCheck, Phone } from "lucide-react";
import Image from "next/image";
import TrueFocus from "@/components/TrueFocus";

const schema = z.object({
  mobile: z
    .string()
    .min(10, "شماره موبایل حداقل ۱۰ رقم است")
    .max(11, "شماره موبایل حداکثر ۱۱ رقم است")
    .transform((v) => convertPersianToEnglish(v))
    .refine(
      (v) => /^09\d{9}$/.test(v) || /^9\d{9}$/.test(v),
      "فرمت صحیح موبایل: 09xxxxxxxxx"
    )
    .transform((v) => (v.startsWith("9") ? `0${v}` : v)),
});

type FormData = z.infer<typeof schema>;

export default function LoginView() {
  const router = useRouter();
  const [loading, setLoading] = React.useState(false);

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: { mobile: "" },
    mode: "onTouched",
  });

  // فقط اعداد (فارسی/عربی/انگلیسی) + تبدیل لحظه‌ای به انگلیسی
  function allowDigits(e: React.ChangeEvent<HTMLInputElement>) {
    const v = e.target.value;
    const reg = /^[0-9\u06F0-\u06F9\u0660-\u0669]*$/;
    if (reg.test(v)) {
      setValue("mobile", convertPersianToEnglish(v), { shouldValidate: true });
    }
  }

  const onSubmit = async (data: FormData) => {
    setLoading(true);
    showToast("لطفاً صبر کنید…", "info");
    try {
      await post("/user-message", { user_mobile: data.mobile });
      showToast("کد تایید برای شما ارسال شد.", "success");
      router.push(`/code?mobile=${data.mobile}`);
    } catch (err) {
      handleErrors(err);
      setLoading(false);
    } 
  };

  return (
    <div
      dir="rtl"
      className="min-h-[100dvh] grid place-items-center bg-gradient-to-br from-background via-background to-muted/40 px-4"
    >
      <div className="pointer-events-none fixed inset-0 [mask-image:radial-gradient(40rem_40rem_at_80%_-10%,black,transparent)]">
        <div className="absolute -top-24 -left-24 h-72 w-72 rounded-full blur-3xl opacity-20 bg-primary" />
        <div className="absolute -bottom-24 -right-24 h-72 w-72 rounded-full blur-3xl opacity-20 bg-indigo-500" />
      </div>
      <Card className="w-full max-w-md border-0 shadow-xl bg-background/70 backdrop-blur-md rounded-2xl">
        <div className="text-center text-xs text-muted-foreground">
          <Image className="mx-auto" height={100} width={100} alt="admin" src={"/logo-final.svg"} />
        </div>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-center gap-3">
            <div className="inline-flex h-10 w-10 items-center justify-center rounded-2xl bg-primary/10">
              <ShieldCheck className="h-5 w-5" />
            </div>
            <CardTitle className="text-center text-xl">ورود به پنل مدیریت  {process.env.NEXT_PUBLIC_TITLE_SITE || ""}</CardTitle>
          </div>
          <p className="mt-2 text-center text-sm text-muted-foreground">
            برای دریافت کد تأیید، شماره موبایل خود را وارد کنید.
          </p>
          <div className="mt-3 flex items-center justify-center gap-2 text-xs text-muted-foreground">
            <span>راهنمای ورود</span>
            <HelpHint side="bottom" content="فقط شماره موبایل مدیران مجاز است. پس از ارسال، کد تأیید پیامکی را در مرحله بعد وارد کنید." />
          </div>
        </CardHeader>

        <CardContent className="pt-2">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
            <div className="space-y-2">
              <label className="text-sm text-muted-foreground">موبایل</label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  {...register("mobile")}
                  inputMode="numeric"
                  placeholder="مثلاً 09123456789"
                  onChange={allowDigits}
                  className="pl-10 text-base"
                />
              </div>
              {errors.mobile && (
                <p className="text-xs text-destructive">{errors.mobile.message}</p>
              )}
            </div>

            <Button
              type="submit"
              className="w-full rounded-xl h-11"
              disabled={loading || isSubmitting}
            >
              {loading || isSubmitting ? (
                <span className="inline-flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  در حال ارسال…
                </span>
              ) : (
                "دریافت کد"
              )}
            </Button>


          </form>
        </CardContent>
      </Card>
    </div>
  );
}